import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  TextField,
  LinearProgress,
  Alert,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

const PHASE_LABELS = {
  starting: 'Starting update...',
  downloading: 'Downloading firmware...',
  uploading: 'Uploading firmware...',
  verifying: 'Waiting for device to finish writing to flash...',
  committing: 'Committing update...',
  rebooting: 'Device is rebooting...',
};

/**
 * Admin-only firmware flashing from a published API build. Lets an admin pick
 * any version the API offers — including an OLDER release than what's installed
 * — download it and flash it over the same Web Serial OTA path used elsewhere.
 */
export default function FirmwareVersionFlashCard({ versions, onFlashVersion, disabled, loading }) {
  const [selected, setSelected] = useState('');
  const [flashing, setFlashing] = useState(false);
  const [phase, setPhase] = useState(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleProgress = useCallback((ph, received, total) => {
    setPhase(ph);
    setProgress(total > 0 ? Math.round((received / total) * 100) : 0);
  }, []);

  const handleFlash = useCallback(async () => {
    if (!selected || !onFlashVersion) return;
    setFlashing(true);
    setError(null);
    setSuccess(false);
    setPhase('starting');
    setProgress(0);

    try {
      await onFlashVersion(selected, handleProgress);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Firmware update failed');
    } finally {
      setFlashing(false);
      setPhase(null);
    }
  }, [selected, onFlashVersion, handleProgress]);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Install a Specific Version (admin)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick any published build — including an older release — and flash it to
          the connected device.
        </Typography>

        {loading && (
          <Typography variant="body2" color="text.secondary">
            Loading available versions...
          </Typography>
        )}

        {!loading && versions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No firmware versions available from the API.
          </Typography>
        )}

        {!loading && versions.length > 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                select
                label="Firmware version"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                SelectProps={{ native: true, inputProps: { 'data-testid': 'firmware-version-select' } }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 260 }}
              >
                <option value="" disabled>
                  Select a version
                </option>
                {versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    v{v.version}
                    {v.prerelease ? ' (prerelease)' : ''}
                  </option>
                ))}
              </TextField>
            </Box>

            <Button
              variant="contained"
              startIcon={<SystemUpdateAltIcon />}
              onClick={handleFlash}
              disabled={disabled || flashing || !selected}
            >
              {flashing ? 'Flashing...' : 'Flash Selected Version'}
            </Button>
          </>
        )}

        {flashing && phase && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {PHASE_LABELS[phase] || phase}
            </Typography>
            <LinearProgress
              variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
              value={progress}
            />
            {phase === 'uploading' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {progress}%
              </Typography>
            )}
          </Box>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Firmware flashed successfully! Device is rebooting.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

FirmwareVersionFlashCard.propTypes = {
  versions: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string.isRequired,
      prerelease: PropTypes.bool,
    })
  ).isRequired,
  onFlashVersion: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};
